import { Box, Button, Typography, useMediaQuery } from "@mui/material";
import parse from 'html-react-parser';
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import alertIcon from '../../../assets/images/triangle-exclamation.png';
import ModalFeedBack from "../../../components/ModalFeedBack";
import { AceiteTermoPAVirtual } from "../../../interfaces/entities/AceiteTermoPAVirtual";
import { CidadeTriagem } from "../../../interfaces/entities/CidadeTriagem";
import { DisponibilidadePAVirtual } from "../../../interfaces/entities/DisponibilidadePAVirtual";
import { Horarios } from "../../../interfaces/entities/Horarios";
import { AgendamentoPaVirtualResponse } from "../../../interfaces/entities/PAVirtual/AgendamentoPaVirtualResponse";
import { ParametrosAgendamentoPaVirtual } from "../../../interfaces/entities/PAVirtual/ParametrosAgendamentoPaVirtual";
import { TipoAtendRetorno } from "../../../interfaces/entities/PAVirtual/TipoAtendRetorno";
import { TipoOrientacaoTriagem } from "../../../interfaces/entities/TipoOrientacaoTriagem";
import { TypeScreeningTabsEnum } from "../../../interfaces/enum/TypeScreeningTabsEnum";
import { agendamentoPaVirtual, agendarPaVirtualAgenda, getDirecionamento, getDisponibilidadePaVirtual, retornaAceiteTermoPaVirtual } from "../../../services/SaudeOnlineService";
import { capitalizeMessage } from "../../../utils/StringUtils";
import AdviceContainerPaVirtual from "./AdviceContainer";
import ScreeningTabs from "./ScreeningTabs";
import StartScreening from "./StartScreening";
import StepConfirmacaoDados from "./StepConfirmacaoDados";
import StepDetalheSintoma from "./StepDetalheSintoma";
import StepLocalExames from "./StepLocalExames";
import StepSintoma from "./StepSintoma";
import TermoDeUsoPaVirtual from "./TermoDeUsoPaVirtual";
import './styles.css';
import { sxButton, sxPaVirtualTitle, sxPaVirtualUnavailable } from "./sxProps";
import { useAccessibility } from "../../../Store/contexts/AccessibilityContext";
import CircularProgressLoading from "../../../components/Accessibility/CircularProgressLoading";
import AudioLoadingDescription from "../../../components/Accessibility/AudioLoadingDescription";
import PageTitle from "../../../components/Accessibility/PageTitle";

const PAVirtual: React.FC = () => {

  const [started, setStarted] = useState<boolean>(false);
  const [sintoma, setSintoma] = useState<TipoOrientacaoTriagem | null>(null);
  const [idCidade, setIdCidade] = useState<string | null>(null);
  const [horario, setHorario] = useState<Horarios | null>(null);
  const [unavailabilityMessage, setUnavailabilityMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [acceptedTermOfUse, setAcceptedTermOfUse] = useState<boolean>(false);
  const [screeningStep, setScreeningStep] = useState<TypeScreeningTabsEnum>(TypeScreeningTabsEnum.SINTOMA);
  const [screeningEnded, setScreeningEnded] = useState<boolean>(false);
  const [tipoAtendPaVirtual, setTipoAtendPaVirtual] = useState<TipoAtendRetorno | null>(null);
  const mobileScreen = useMediaQuery('(max-width: 768px)');
  const navigate = useNavigate();
  const { fontSizeStyleAccessibility, fontSizeTitleStyleAccessibility } = useAccessibility();

  const [modalFeedback, setModalFeedback] = useState<null | {
    icon?: 'error' | 'success' | 'warning', title?: string, textButton?: string, subTitle?: string,
    closeAction: () => void, showButtonModal: boolean, buttonAction?: () => void
  }>(null);

  useEffect(() => {
    validarDisponibilidadePaVirtual();
  }, []);

  const openModalError = (title: string, subtitle: string) => {
    setModalFeedback({
      icon: 'error',
      title: title,
      subTitle: subtitle,
      textButton: 'Fechar',
      showButtonModal: true,
      closeAction: () => setModalFeedback(null),
      buttonAction: () => setModalFeedback(null)
    });
  }

  const openModalSuccess = () => {
    setModalFeedback({
      icon: 'success',
      title: 'Agendado',
      subTitle: 'Seu agendamento foi realizado sucesso!',
      showButtonModal: false,
      closeAction: () => {
        setModalFeedback(null);
        navigate('/saudeOnline/meusAgendamentos');
      }
    });
  }

  const validarDisponibilidadePaVirtual = async () => {
    setLoading(true);
    const result = await getDisponibilidadePaVirtual();

    if (result) {
      const disponibilidade: DisponibilidadePAVirtual = result.data;
      if (disponibilidade.status === 'NOK') {
        setUnavailabilityMessage(disponibilidade.mensagem);
      }
    } else {
      setUnavailabilityMessage('Não foi possível verificar a disponibilidade do serviço PA Virtual. Tente novamente em instantes.');
    }

    setLoading(false)
  }

  const start = async () => {
    setLoading(true);
    const result = await retornaAceiteTermoPaVirtual();
    if (result?.status === 200) {
      const aceiteTermo: AceiteTermoPAVirtual = result.data;
      if (aceiteTermo.indAceite === 'S') {
        setAcceptedTermOfUse(true);
      }
      setStarted(true);
    } else {
      openModalError("Erro", "Falha ao consultar termo de responsabilidade. Tente novamente em instantes.");
    }
    setLoading(false);
  }

  const handleAcceptTermsOfUse = () => {
    setAcceptedTermOfUse(true);
  }

  const handleSintoma = async (sintoma: TipoOrientacaoTriagem) => {
    setSintoma(sintoma);
    if (sintoma.codTipoTeleOrientacao === 'NENHUM') {
      setScreeningStep(TypeScreeningTabsEnum.LOCAL_EXAMES);
      setLoading(true);
      const result = await getDirecionamento('', 'NENHUM');
      if (result?.status === 200) {
        const tipoAtendRetorno: TipoAtendRetorno = result.data;
        setTipoAtendPaVirtual(tipoAtendRetorno);
      } else {
        openModalError('Erro', 'Ocorreu um erro ao prosseguir com a triagem. Tente novamente em instantes.');
      }
      setLoading(false);
    } else {
      setScreeningStep(TypeScreeningTabsEnum.DETALHE_SINTOMA);
    }
  }

  const handleLocalExames = (cidade: CidadeTriagem | null, horarioAgendamentoInfantil: Horarios | null) => {
    setIdCidade(cidade?.idCidade || null);
    setHorario(horarioAgendamentoInfantil);
    setScreeningStep(TypeScreeningTabsEnum.CONFIRMACAO_DADOS);
  }

  const iniciarFilaPaVirtual = async (cel: string, email: string) => {
    const body = buildAgendamentoPaVirtualBody(cel, email, idCidade, undefined);
    const result = await agendamentoPaVirtual(body);
    if (result?.status === 200) {
      const retAgendamento: AgendamentoPaVirtualResponse = result.data;
      if (retAgendamento.mensagem === 'INDISPONIVEL' || retAgendamento.mensagem === 'NEGADA') {
        handleAgendamentoPaVirtualNOK(retAgendamento);
      } else {
        navigate(`/saudeOnline/ingressarNoAtendimento/${retAgendamento.idAgenda}/paVirtual`);
      }
    } else {
      openModalError("Erro", "Falha ao prosseguir com a triagem. Tente novamente em instantes.");
    }
  }

  const agendarPaVirtualInfantil = async (cel: string, email: string) => {
    const body = buildAgendamentoPaVirtualBody(cel, email, idCidade, horario || undefined);
    const result = await agendarPaVirtualAgenda(body);
    if (result?.status === 200) {
      const retAgendamento: AgendamentoPaVirtualResponse = result.data;
      if (retAgendamento.mensagem === 'INDISPONIVEL' || retAgendamento.mensagem === 'NEGADA') {
        handleAgendamentoPaVirtualNOK(retAgendamento);
      } else {
        openModalSuccess();
      }
    } else {
      openModalError("Erro", !!result?.data?.mensagem ? capitalizeMessage(result.data.mensagem) : "Falha ao prosseguir com a triagem. Tente novamente em instantes.");
    }
  }

  const buildAgendamentoPaVirtualBody = (
    cel: string, email: string, idCidade: string | null, horario: Horarios | undefined
  ): ParametrosAgendamentoPaVirtual => {
    return {
      codProtocAtend: sintoma!.codTipoTeleOrientacao,
      idCidadeExame: idCidade || "",
      email: email,
      cel: cel,
      data: horario?.data,
      hora: horario?.hora,
      idInstal: horario?.idInstal,
      idPrestador: horario?.idPrestador
    };
  };

  const handleAgendamentoPaVirtualNOK = (response: AgendamentoPaVirtualResponse) => {
    if (response.mensagem === 'INDISPONIVEL') {
      setUnavailabilityMessage(`<b>Caro(a) cliente,</b><br>Compreendemos a sua necessidade, 
        porém devido à alta procura, todos os nossos agendamentos estão indisponíveis no momentos. Solicitamos a gentileza de retornar mais tarde.`);
    } else if (response.mensagem === 'NEGADA') {
      setUnavailabilityMessage('<b>Atendimento não autorizado.</b><br>Para mais informações, entre em contato com a Unimed Campinas pelo 0800 013 66 88.');
    }
  }

  const redirectScreening = async (cel: string, email: string) => {
    setLoading(true);
    if (tipoAtendPaVirtual === 'VIRTUAL-AGENDA') {
      await agendarPaVirtualInfantil(cel, email);
    } else {
      await iniciarFilaPaVirtual(cel, email);
    }
    setLoading(false);
  }

  const handleBackStepLocalExames = () => {
    if (sintoma?.codTipoTeleOrientacao === 'NENHUM') {
      setScreeningStep(TypeScreeningTabsEnum.SINTOMA);
    } else {
      setScreeningStep(TypeScreeningTabsEnum.DETALHE_SINTOMA);
    }
  }

  return (
    <div className='pa-virtual' aria-atomic>
      <AudioLoadingDescription loading={loading}/>
      <PageTitle text={!started ? 'Pronto atendimento virtual | Unimed ágil' : 'Triagem de atendimento'} />
      <div className='margin-bottom-16' />
      {loading ? <CircularProgressLoading/> :
        <>
          {unavailabilityMessage ?
            <Box
              display={'flex'} flexDirection={'column'} margin={'30px 0px'}
              alignItems={'flex-start'} gap={'30px'}
            >
              <img src={alertIcon} alt='Ícone de aviso.' />
              <Typography style={fontSizeStyleAccessibility} sx={sxPaVirtualUnavailable}>{parse(unavailabilityMessage)}</Typography>
            </Box>
            :
            <>
              {!started &&
                <StartScreening start={start} />
              }
              {started &&
                <>
                  {!acceptedTermOfUse ?
                    <TermoDeUsoPaVirtual backStep={() => setStarted(false)} handleTermsOfUse={handleAcceptTermsOfUse} />
                    :
                    <>
                      {!screeningEnded ?
                        <>
                          <ScreeningTabs step={screeningStep} backStep={setScreeningStep} />
                          {screeningStep === TypeScreeningTabsEnum.SINTOMA &&
                            <StepSintoma
                              backStep={() => setStarted(false)}
                              nextStep={handleSintoma}
                            />}
                          {screeningStep === TypeScreeningTabsEnum.DETALHE_SINTOMA &&
                            <StepDetalheSintoma
                              backStep={() => setScreeningStep(TypeScreeningTabsEnum.SINTOMA)} sintoma={sintoma}
                              nextStep={() => setScreeningStep(TypeScreeningTabsEnum.LOCAL_EXAMES)}
                              endScreening={() => setScreeningEnded(true)}
                              setTipoAtendPaVirtual={(tipoAtend) => setTipoAtendPaVirtual(tipoAtend)}
                            />}
                          {screeningStep === TypeScreeningTabsEnum.LOCAL_EXAMES &&
                            <StepLocalExames
                              backStep={handleBackStepLocalExames} sintoma={sintoma}
                              nextStep={handleLocalExames}
                              tipoAtendPaVirtual={tipoAtendPaVirtual!}
                            />}
                          {screeningStep === TypeScreeningTabsEnum.CONFIRMACAO_DADOS &&
                            <StepConfirmacaoDados
                              backStep={() => setScreeningStep(TypeScreeningTabsEnum.LOCAL_EXAMES)}
                              endScreening={redirectScreening}
                              sintoma={sintoma}
                              tipoAtendPaVirtual={tipoAtendPaVirtual!}
                            />
                          }
                        </>
                        :
                        <AdviceContainerPaVirtual />
                      }
                    </>
                  }
                </>
              }
            </>
          }
        </>
      }
      {(unavailabilityMessage || screeningEnded) &&
        <Button
          variant="contained" size="large"
          onClick={() => navigate('/home')}
          sx={sxButton} fullWidth={mobileScreen}
          style={fontSizeStyleAccessibility} tabIndex={0}
        >
          VOLTAR PARA O INÍCIO
        </Button>}
      <ModalFeedBack
        icon={modalFeedback?.icon as 'success' | 'warning' | 'error'}
        showModal={!!modalFeedback}
        showButtonModal={modalFeedback?.showButtonModal}
        handleButtonModal={modalFeedback?.buttonAction}
        handleShowModal={modalFeedback?.closeAction}
        textButton="FECHAR"
        title={modalFeedback?.title as string} subTitle={modalFeedback?.subTitle as string}
      />
    </div>
  )
}

export default PAVirtual;